import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Printer, Search, Package, Store, MapPin, Euro, Calendar, Wrench, Camera, Move, AlertTriangle, LifeBuoy, Boxes, ChevronDown, Trash2 } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { Can } from '../lib/usePermissions';
import { inventoryApi, brandsApi, storesApi, usersApi } from '../lib/api';
import { useAssignees, groupAssignees } from '../lib/useAssignees';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { KitsPanel } from '../components/KitsPanel';
import InventoryAgeReport from '../components/InventoryAgeReport';
import { toast } from 'sonner';

const SCOPED_TO_OPS = (role) => role === 'operations_manager';

// Calcola lo stato manutenzione per ciascun item dato lo storico eventi
// "Mant. recente" se ultima manutenzione/ricambio ≤ 90 giorni
function maintenanceStatus(item) {
  const last = item.last_maintenance_at;
  if (!last) return { label: 'Nessuna mant.', tone: 'neutral' };
  const daysAgo = (Date.now() - new Date(last).getTime()) / (1000 * 60 * 60 * 24);
  if (daysAgo <= 90) return { label: 'Mant. recente', tone: 'fresh' };
  return { label: 'Mant. vecchia', tone: 'warn' };
}

export default function InventoryPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total_items: 0, active_stores: 0, total_value: 0, maintenance_count: 0 });
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [stores, setStores] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterBrand, setFilterBrand] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStore, setFilterStore] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [showCreate, setShowCreate] = useState(false);
  const [showKits, setShowKits] = useState(false);

  const isOps = SCOPED_TO_OPS(user?.role);
  const isAM = user?.role === 'area_manager';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [it, st_kpi, cat, br, st, us] = await Promise.all([
        inventoryApi.listItems().then(r => r.data),
        inventoryApi.stats().then(r => r.data).catch(() => null),
        inventoryApi.categories().then(r => r.data),
        brandsApi.list().then(r => r.data),
        storesApi.list({ include_special: true }).then(r => r.data),
        (isOps || isAM) ? usersApi.list().then(r => r.data).catch(() => []) : Promise.resolve([]),
      ]);
      setItems(it || []);
      if (st_kpi) setStats(st_kpi);
      setCategories(cat || []);
      setBrands(br || []);
      setStores(Array.isArray(st) ? st : (st?.stores || []));
      setUsers(us || []);
    } catch (e) {
      toast.error('Errore nel caricamento del magazzino');
    } finally {
      setLoading(false);
    }
  }, [isOps, isAM]);

  useEffect(() => { load(); }, [load]);

  const storeMap = useMemo(
    () => Object.fromEntries(stores.map(s => [s.store_id, s.display_name || s.name])),
    [stores]
  );

  // Sede speciale (Magazzino/Accademia): in fondo ai dropdown con label
  // distintiva. Non sono saloni e l'utente le riconosce a colpo d'occhio.
  const sortedStores = useMemo(() => {
    const isSpecial = (s) => s.kind === 'warehouse' || s.kind === 'academy';
    return [...stores].sort((a, b) => {
      if (isSpecial(a) !== isSpecial(b)) return isSpecial(a) ? 1 : -1;
      return (a.display_name || '').localeCompare(b.display_name || '');
    });
  }, [stores]);
  const storeOptions = useMemo(() => sortedStores.map(s => ({
    id: s.store_id,
    name: s.display_name || s.name,
    kind: s.kind || 'store',
  })), [sortedStores]);

  const filtered = useMemo(() => {
    return items.filter(it => {
      if (filterBrand && it.brand_id !== filterBrand) return false;
      if (filterCategory && it.category_id !== filterCategory) return false;
      if (filterStore && it.store_id !== filterStore) return false;
      if (search) {
        const q = search.toLowerCase();
        const storeName = storeMap[it.store_id] || '';
        const hay = `${it.code} ${it.model || ''} ${it.description || ''} ${it.category_name || ''} ${storeName}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [items, filterBrand, filterCategory, filterStore, search, storeMap]);

  // Raggruppa gli oggetti creati da un kit in "folder" (feedback stakeholder 2026-07-24)
  const { kitGroups, looseItems } = useMemo(() => {
    const groups = new Map();
    const loose = [];
    filtered.forEach(it => {
      const key = it.kit_instance_id || (it.kit_id ? `${it.kit_id}|${it.store_id || ''}|${it.station_id || ''}` : null);
      if (key) {
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(it);
      } else {
        loose.push(it);
      }
    });
    return { kitGroups: [...groups.entries()], looseItems: loose };
  }, [filtered]);

  async function extractFromKit(item) {
    try {
      await inventoryApi.removeFromKit(item.item_id);
      toast.success(`${item.code} estratto dal kit`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore estrazione dal kit');
    }
  }

  async function bulkDelete() {
    if (!window.confirm(`Eliminare definitivamente ${selected.size} ${selected.size === 1 ? 'oggetto' : 'oggetti'}? Verrà cancellato anche lo storico eventi.`)) return;
    try {
      const res = await inventoryApi.bulkDelete(Array.from(selected));
      const del = res.data?.deleted ?? 0;
      const skipped = res.data?.skipped?.length ?? 0;
      if (skipped > 0) toast.warning(`${del} eliminati, ${skipped} saltati (fuori giurisdizione o non trovati)`);
      else toast.success(`${del} ${del === 1 ? 'oggetto eliminato' : 'oggetti eliminati'}`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore eliminazione');
    }
  }

  function toggle(id) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function printLabels() {
    if (selected.size === 0) {
      toast.error('Seleziona almeno un oggetto per stampare l\'etichetta');
      return;
    }
    try {
      const res = await inventoryApi.labelsPdf(Array.from(selected));
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `etichette-${Date.now()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Errore generazione etichette');
    }
  }

  const [showReassign, setShowReassign] = useState(false);

  async function bulkReassign({ storeId, assignedUserId, clearAssignment }) {
    try {
      const payload = { item_ids: Array.from(selected) };
      if (storeId) payload.store_id = storeId;
      if (assignedUserId) payload.assigned_user_id = assignedUserId;
      if (clearAssignment) payload.clear_assignment = true;
      const res = await inventoryApi.bulkReassign(payload);
      const updated = res.data?.updated ?? 0;
      const skipped = res.data?.skipped?.length ?? 0;
      if (skipped > 0) {
        toast.warning(`${updated} aggiornati, ${skipped} saltati (fuori giurisdizione o non trovati)`);
      } else {
        toast.success(`${updated} ${updated === 1 ? 'oggetto spostato' : 'oggetti spostati'}`);
      }
      setSelected(new Set());
      setShowReassign(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore riassegnazione');
    }
  }

  // Brand badge in alto a sinistra (prende il primo brand attivo dell'utente o "LIB" default)
  const brandBadge = (brands[0]?.code || 'LIB').slice(0, 3).toUpperCase();

  return (
    <div className="p-4 md:p-8 max-w-[1920px] space-y-8" data-testid="inventory-page">
      {/* ===== HEADER ===== */}
      <header className="flex items-start justify-between gap-4 flex-wrap pb-6 border-b border-slate-200">
        <div className="flex items-start gap-4">
          <div
            className="h-12 w-12 rounded-md border border-slate-300 bg-slate-50 flex items-center justify-center text-xs font-bold tracking-wider text-slate-700 shrink-0"
            data-testid="brand-badge"
          >
            {brandBadge}
          </div>
          <div>
            <h2 className="font-heading text-3xl font-bold tracking-tight">Registro Strumentazione</h2>
            <p className="text-sm text-slate-500 mt-1">
              Monitoraggio attrezzature — rete saloni
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => navigate('/assistance?new=1')}
            data-testid="open-ticket-btn"
            className="rounded-md"
          >
            <LifeBuoy className="w-4 h-4 mr-2" />Apri ticket assistenza
          </Button>
          <Can permission="inventory.view">
            <Button variant="outline" onClick={() => setShowKits(true)} data-testid="kits-btn" className="rounded-md">
              <Boxes className="w-4 h-4 mr-2" />Kit
            </Button>
          </Can>
          <Can permission="inventory.item.create">
            <Button onClick={() => setShowCreate(true)} data-testid="new-item-btn" className="rounded-md">
              <Plus className="w-4 h-4 mr-2" />Nuovo
            </Button>
          </Can>
        </div>
      </header>

      {showKits && (
        <Dialog open onOpenChange={(v) => !v && setShowKits(false)}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="kits-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <Boxes className="w-5 h-5" /> Kit di strumenti
              </DialogTitle>
              <DialogDescription>Crea kit dal catalogo e assegnali a negozi e postazioni.</DialogDescription>
            </DialogHeader>
            <KitsPanel categories={categories} stores={stores} onItemsCreated={() => { setShowKits(false); load(); }} />
          </DialogContent>
        </Dialog>
      )}

      {/* ===== KPI STAT CARDS ===== */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          label="Totale oggetti"
          value={stats.total_items}
          testId="stat-total"
        />
        <StatCard
          label="Negozi attivi"
          value={stats.active_stores}
          testId="stat-stores"
        />
        <StatCard
          label="Valore totale"
          value={`€${Math.round(stats.total_value).toLocaleString('it-IT')}`}
          testId="stat-value"
        />
        <StatCard
          label="Manutenzioni"
          value={stats.maintenance_count}
          testId="stat-maintenance"
        />
      </div>

      {/* ===== SEARCH + FILTRI COMPATTI ===== */}
      <div className="space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            data-testid="inventory-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca per codice, nome, negozio..."
            className="pl-10 h-11 rounded-md"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <CompactFilter label="Brand" value={filterBrand} onChange={setFilterBrand}
                         options={brands.map(b => ({ id: b.brand_id, name: b.name }))} />
          <CompactFilter label="Categoria" value={filterCategory} onChange={setFilterCategory}
                         options={categories.map(c => ({ id: c.category_id, name: c.name }))} />
          <CompactFilter label="Negozio" value={filterStore} onChange={setFilterStore}
                         options={storeOptions} />
          {(filterBrand || filterCategory || filterStore) && (
            <button
              onClick={() => { setFilterBrand(''); setFilterCategory(''); setFilterStore(''); }}
              className="text-xs text-slate-500 hover:text-slate-800 underline px-2"
              data-testid="clear-filters"
            >
              Reset filtri
            </button>
          )}
        </div>
      </div>

      {/* ===== LISTA CARD ===== */}
      <div className="space-y-3">
        {loading ? (
          <div className="p-8 text-center text-slate-500 border rounded-md bg-white">Caricamento…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center border rounded-md bg-white">
            <Package className="w-12 h-12 mx-auto text-slate-300" />
            <p className="mt-3 text-slate-600">Nessun oggetto trovato.</p>
            <Can permission="inventory.item.create">
              <Button onClick={() => setShowCreate(true)} variant="link" className="mt-2">
                Crea il primo oggetto
              </Button>
            </Can>
          </div>
        ) : (
          <>
            {kitGroups.map(([key, groupItems]) => (
              <KitFolderCard
                key={key}
                groupKey={key}
                items={groupItems}
                storeMap={storeMap}
                selected={selected}
                onToggleSelect={toggle}
                onOpenItem={(it) => navigate(`/inventory/${it.item_id}`, {
                  state: { backTo: '/inventory', backLabel: 'Inventario' },
                })}
                onExtract={extractFromKit}
                canSelect={isOps || isAM}
                selectionActive={selected.size > 0}
              />
            ))}
            {looseItems.map(it => (
              <ItemRowCard
                key={it.item_id}
                item={it}
                storeName={storeMap[it.store_id]}
                isSelected={selected.has(it.item_id)}
                onToggleSelect={() => toggle(it.item_id)}
                onOpen={() => navigate(`/inventory/${it.item_id}`, {
                  state: { backTo: '/inventory', backLabel: 'Inventario' },
                })}
                canSelect={isOps || isAM}
                selectionActive={selected.size > 0}
              />
            ))}
          </>
        )}
      </div>

      {/* ===== ETÀ MEDIA ATTREZZI (fondo pagina) ===== */}
      <InventoryAgeReport />

      {/* ===== OVERLAY FLOTTANTE BULK ACTIONS ===== */}
      {selected.size > 0 && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-slate-900 text-white rounded-full shadow-2xl px-5 py-3 flex items-center gap-4 animate-in fade-in slide-in-from-bottom-4"
          data-testid="bulk-selection-overlay"
        >
          <span className="text-sm font-medium" data-testid="bulk-selection-count">
            {selected.size} {selected.size === 1 ? 'selezionato' : 'selezionati'}
          </span>
          <span className="h-5 w-px bg-white/20" />
          <button
            onClick={printLabels}
            className="inline-flex items-center gap-2 text-sm font-medium hover:text-emerald-300 transition"
            data-testid="bulk-print-labels-btn"
          >
            <Printer className="w-4 h-4" />Stampa etichette
          </button>
          {(isOps || isAM) && (
            <>
              <span className="h-5 w-px bg-white/20" />
              <button
                onClick={() => setShowReassign(true)}
                className="inline-flex items-center gap-2 text-sm font-medium hover:text-emerald-300 transition"
                data-testid="bulk-reassign-btn"
              >
                <Move className="w-4 h-4" />Sposta
              </button>
              <span className="h-5 w-px bg-white/20" />
              <button
                onClick={bulkDelete}
                className="inline-flex items-center gap-2 text-sm font-medium text-red-300 hover:text-red-400 transition"
                data-testid="bulk-delete-btn"
              >
                <Trash2 className="w-4 h-4" />Elimina
              </button>
            </>
          )}
          <span className="h-5 w-px bg-white/20" />
          <button
            onClick={() => setSelected(new Set())}
            className="text-sm text-slate-300 hover:text-white transition"
            data-testid="bulk-clear-btn"
            aria-label="Annulla selezione"
          >
            Annulla
          </button>
        </div>
      )}

      {showCreate && (
        <CreateItemDialog
          open={showCreate}
          onClose={() => setShowCreate(false)}
          onCreated={(item) => { setShowCreate(false); load(); navigate(`/inventory/${item.item_id}`); }}
          brands={brands}
          categories={categories}
          stores={sortedStores}
          users={users}
        />
      )}

      {showReassign && (
        <BulkReassignDialog
          count={selected.size}
          stores={sortedStores}
          users={users}
          isOps={isOps}
          onCancel={() => setShowReassign(false)}
          onConfirm={bulkReassign}
        />
      )}
    </div>
  );
}

// ============================================================
// Sub-components
// ============================================================

function KitFolderCard({ groupKey, items, storeMap, selected, onToggleSelect, onOpenItem, onExtract, canSelect, selectionActive }) {
  const [open, setOpen] = useState(false);
  const first = items[0];
  const name = first.kit_name || 'Kit';
  const value = items.reduce((s, it) => s + (Number(it.purchase_price) || 0), 0);
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid={`kit-folder-${groupKey}`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 p-4 hover:bg-slate-50 text-left transition-colors"
        data-testid={`kit-folder-toggle-${groupKey}`}
      >
        <div className="h-10 w-10 rounded-md bg-slate-900 text-white flex items-center justify-center shrink-0">
          <Boxes className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-heading font-bold text-slate-900 truncate">{name}</p>
          <p className="text-xs text-slate-500 mt-0.5">
            {storeMap[first.store_id] || 'Nessun negozio'} · {items.length} {items.length === 1 ? 'oggetto' : 'oggetti'}
            {value > 0 && <> · €{value.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</>}
          </p>
        </div>
        <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="border-t border-slate-100 p-3 space-y-2 bg-slate-50/60" data-testid={`kit-folder-items-${groupKey}`}>
          {items.map(it => (
            <div key={it.item_id} className="space-y-1">
              <ItemRowCard
                item={it}
                storeName={storeMap[it.store_id]}
                isSelected={selected.has(it.item_id)}
                onToggleSelect={() => onToggleSelect(it.item_id)}
                onOpen={() => onOpenItem(it)}
                canSelect={canSelect}
                selectionActive={selectionActive}
              />
              {canSelect && (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => onExtract(it)}
                    className="text-[11px] text-slate-400 hover:text-slate-800 underline underline-offset-2"
                    data-testid={`kit-extract-${it.item_id}`}
                  >
                    Estrai dal kit
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, testId }) {
  return (
    <div
      className="bg-white border border-slate-200 rounded-lg p-5"
      data-testid={testId}
    >
      <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">{label}</p>
      <p className="mt-3 text-3xl font-bold tracking-tight text-slate-900 font-mono">{value}</p>
    </div>
  );
}

function CompactFilter({ label, value, onChange, options }) {
  return (
    <Select value={value || 'all'} onValueChange={(v) => onChange(v === 'all' ? '' : v)}>
      <SelectTrigger className="h-9 w-auto min-w-[140px] rounded-full bg-white text-xs px-3" data-testid={`filter-${label.toLowerCase()}`}>
        <SelectValue placeholder={label} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">Tutti i {label.toLowerCase()}</SelectItem>
        {options.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function ItemRowCard({ item, storeName, isSelected, onToggleSelect, onOpen, canSelect, selectionActive }) {
  const status = maintenanceStatus(item);
  const pillClass =
    status.tone === 'fresh' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : status.tone === 'warn' ? 'bg-amber-50 text-amber-700 border-amber-200'
    : 'bg-slate-50 text-slate-600 border-slate-200';

  // Checkbox: nascosta finché nessuno è selezionato e mouse non passa sopra.
  // Diventa visibile su hover, oppure sempre visibile non appena scatta la modalità "selezione attiva".
  const checkboxVisibility = selectionActive || isSelected
    ? 'opacity-100'
    : 'opacity-0 group-hover:opacity-100 focus-within:opacity-100';

  return (
    <div
      className="group bg-white border border-slate-200 rounded-lg p-5 hover:border-slate-400 hover:shadow-sm transition cursor-pointer"
      data-testid={`inventory-card-${item.code}`}
      onClick={onOpen}
    >
      <div className="flex items-start gap-4">
        {canSelect && (
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => { e.stopPropagation(); onToggleSelect(); }}
            onClick={(e) => e.stopPropagation()}
            className={`mt-1 w-4 h-4 accent-slate-900 transition-opacity duration-150 ${checkboxVisibility}`}
            data-testid={`select-${item.code}`}
            aria-label={`Seleziona ${item.code}`}
          />
        )}
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="inline-block px-2.5 py-1 rounded-md bg-slate-100 border border-slate-200 text-xs font-mono font-semibold text-slate-700 tracking-wider">
              {item.code}
            </span>
            <h3 className="font-heading text-lg font-bold text-slate-900 leading-tight">
              {item.model || item.category_name}
            </h3>
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-slate-600">
            {storeName && (
              <span className="inline-flex items-center gap-1.5">
                <Store className="w-3.5 h-3.5 text-slate-400" />{storeName}
              </span>
            )}
            {item.position && (
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-slate-400" />{item.position}
              </span>
            )}
            {item.purchase_price != null && (
              <span className="inline-flex items-center gap-1.5">
                <Euro className="w-3.5 h-3.5 text-slate-400" />{Number(item.purchase_price).toLocaleString('it-IT', { minimumFractionDigits: 2 })}
              </span>
            )}
            {item.purchase_date && (
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-slate-400" />
                {new Date(item.purchase_date).toLocaleDateString('it-IT')}
              </span>
            )}
            {item.maintenance_count > 0 && (
              <span className="inline-flex items-center gap-1.5">
                <Wrench className="w-3.5 h-3.5 text-slate-400" />{item.maintenance_count} manutenzioni
              </span>
            )}
            {item.photo_count > 0 && (
              <span
                className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-slate-900 text-white text-[11px] font-medium"
                title="Foto dell'oggetto + storico manutenzioni"
                data-testid={`photos-badge-${item.code}`}
              >
                <Camera className="w-3 h-3" />{item.photo_count} foto
              </span>
            )}
            {item.maintenance_alert?.status === 'due' && (
              <span
                className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300 text-[11px] font-medium"
                title={`Manutenzione consigliata: ogni ${item.maintenance_alert.recommended_months} mesi`}
                data-testid={`maintenance-due-badge-${item.code}`}
              >
                <AlertTriangle className="w-3 h-3" />Manutenzione consigliata
              </span>
            )}
            {item.maintenance_alert?.status === 'overdue' && (
              <span
                className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-red-100 text-red-800 border border-red-300 text-[11px] font-medium"
                title={`Manutenzione consigliata: ogni ${item.maintenance_alert.recommended_months} mesi`}
                data-testid={`maintenance-overdue-badge-${item.code}`}
              >
                <AlertTriangle className="w-3 h-3" />Manutenzione in ritardo
              </span>
            )}
          </div>
        </div>
        <span
          className={`inline-block px-3 py-1 rounded-full border text-xs font-medium ${pillClass} shrink-0`}
          data-testid={`status-${item.code}`}
        >
          {status.label}
        </span>
      </div>
    </div>
  );
}

// ============================================================
// Create dialog (sezionato come da mockup)
// ============================================================
function CreateItemDialog({ open, onClose, onCreated, brands, categories, stores, users }) {
  const [form, setForm] = useState({
    brand_id: '', category_id: '', code: '', model: '', description: '',
    purchase_date: '', purchase_price: '', store_id: '', position: '', assigned_user_id: '',
  });
  const [saving, setSaving] = useState(false);
  // Refactor 2026-05-29: dropdown popolato da staff_members + manager
  // (vedi useAssignees hook). Sostituisce il vecchio filtro users.role=store_user
  // che in produzione lasciava la lista vuota.
  const { assignees, loading: assigneesLoading } = useAssignees();
  const grouped = groupAssignees(assignees, form.store_id);

  async function submit() {
    if (!form.brand_id || !form.category_id) {
      toast.error('Brand e categoria sono obbligatori');
      return;
    }
    if (!form.store_id) {
      toast.error('Seleziona un negozio');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach(k => { if (payload[k] === '' || payload[k] == null) delete payload[k]; });
      if (payload.purchase_price) payload.purchase_price = Number(payload.purchase_price);
      const res = await inventoryApi.createItem(payload);
      toast.success(`Oggetto creato: ${res.data.code}`);
      onCreated(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore creazione oggetto');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Nuovo oggetto</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-2">
          {/* IDENTIFICAZIONE */}
          <Section title="Identificazione">
            <div className="grid grid-cols-2 gap-4">
              <FormSelect label="Brand *" value={form.brand_id}
                          onChange={(v) => setForm(f => ({ ...f, brand_id: v }))}
                          options={brands.map(b => ({ id: b.brand_id, name: b.name }))} testId="form-brand" />
              <FormSelect label="Categoria prodotto *" value={form.category_id}
                          onChange={(v) => setForm(f => ({ ...f, category_id: v }))}
                          options={categories.map(c => ({ id: c.category_id, name: c.name }))} testId="form-category" />
              <div className="col-span-2">
                <Label className="text-xs">Codice (opzionale)</Label>
                <Input value={form.code} onChange={(e) => setForm(f => ({ ...f, code: e.target.value }))}
                       placeholder="Lascia vuoto per generarlo in automatico (es. LIB-clipper-0001)"
                       data-testid="form-code" />
                <p className="text-[10px] text-slate-400 mt-1">Se compilato, deve essere univoco. Se vuoto, il sistema genera BRAND-CATEGORIA-SERIALE.</p>
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Modello / Marca</Label>
                <Input value={form.model} onChange={(e) => setForm(f => ({ ...f, model: e.target.value }))}
                       placeholder="es. JRL Diamante" data-testid="form-model" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Note aggiuntive</Label>
                <Textarea value={form.description} rows={2}
                          onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                          placeholder="Modello, marca, numero seriale, osservazioni..."
                          data-testid="form-desc" />
              </div>
            </div>
          </Section>

          {/* ACQUISTO */}
          <Section title="Acquisto">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Data di acquisto</Label>
                <Input type="date" value={form.purchase_date}
                       onChange={(e) => setForm(f => ({ ...f, purchase_date: e.target.value }))}
                       data-testid="form-date" />
              </div>
              <div>
                <Label className="text-xs">Prezzo di acquisto (€)</Label>
                <Input type="number" step="0.01" value={form.purchase_price}
                       onChange={(e) => setForm(f => ({ ...f, purchase_price: e.target.value }))}
                       placeholder="0.00"
                       data-testid="form-price" />
              </div>
            </div>
          </Section>

          {/* UBICAZIONE */}
          <Section title="Ubicazione">
            <div className="grid grid-cols-2 gap-4">
              <FormSelect label="Negozio *" value={form.store_id}
                          onChange={(v) => setForm(f => ({ ...f, store_id: v, assigned_user_id: '' }))}
                          options={stores.map(s => ({ id: s.store_id, name: s.display_name || s.name }))}
                          testId="form-store" placeholder="— seleziona negozio —" />
              <div>
                <Label className="text-xs">Postazione</Label>
                <Input value={form.position}
                       onChange={(e) => setForm(f => ({ ...f, position: e.target.value }))}
                       placeholder="es. Postazione 1, Lavabo B..."
                       data-testid="form-position" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Affidato a</Label>
                <Select value={form.assigned_user_id || 'none'}
                        onValueChange={(v) => setForm(f => ({ ...f, assigned_user_id: v === 'none' ? '' : v }))}>
                  <SelectTrigger data-testid="form-assignee">
                    <SelectValue placeholder={assigneesLoading ? 'Caricamento…' : '— nessuno —'} />
                  </SelectTrigger>
                  <SelectContent className="max-h-[60vh]">
                    <SelectItem value="none">— nessuno —</SelectItem>
                    {grouped.sameStoreStaff.length > 0 && (
                      <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-slate-500 font-semibold bg-slate-50 sticky top-0">
                        Staff del negozio
                      </div>
                    )}
                    {grouped.sameStoreStaff.map(a => (
                      <SelectItem key={a.id} value={a.id}>
                        <span className="font-medium">{a.name}</span>
                        {a.role && <span className="ml-2 text-[10px] text-slate-500 uppercase tracking-wider">{a.role}</span>}
                      </SelectItem>
                    ))}
                    {grouped.otherStaff.length > 0 && (
                      <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-slate-500 font-semibold bg-slate-50">
                        Altri staff
                      </div>
                    )}
                    {grouped.otherStaff.map(a => (
                      <SelectItem key={a.id} value={a.id}>
                        <span className="font-medium">{a.name}</span>
                        {a.store_name && <span className="ml-2 text-[10px] text-slate-500">· {a.store_name}</span>}
                      </SelectItem>
                    ))}
                    {grouped.managers.length > 0 && (
                      <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-slate-500 font-semibold bg-slate-50">
                        Manager
                      </div>
                    )}
                    {grouped.managers.map(a => (
                      <SelectItem key={a.id} value={a.id}>
                        <span className="font-medium">{a.name}</span>
                        <span className="ml-2 text-[10px] text-slate-500 uppercase tracking-wider">{a.role}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </Section>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={submit} disabled={saving} data-testid="form-submit">
            {saving ? 'Salvataggio…' : 'Crea oggetto'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h4 className="text-xs uppercase tracking-[0.15em] text-slate-500 font-semibold mb-3 pb-2 border-b border-slate-200">
        {title}
      </h4>
      {children}
    </section>
  );
}

function FormSelect({ label, value, onChange, options, testId, placeholder = 'Seleziona…', allowClear = false }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Select value={value || (allowClear ? 'none' : '')} onValueChange={(v) => onChange(allowClear && v === 'none' ? '' : v)}>
        <SelectTrigger data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
        <SelectContent>
          {allowClear && <SelectItem value="none">— nessuno —</SelectItem>}
          {options.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}


function BulkReassignDialog({ count, stores, users, isOps, onCancel, onConfirm }) {
  const [storeId, setStoreId] = useState('');
  const [assignedUserId, setAssignedUserId] = useState('');
  const [clearAssignment, setClearAssignment] = useState(false);
  const [saving, setSaving] = useState(false);

  // Refactor 2026-05-29: combinazione staff+manager (vedi useAssignees).
  const { assignees, loading: assigneesLoading } = useAssignees();
  const grouped = groupAssignees(assignees, storeId);

  async function handleConfirm() {
    if (!storeId && !assignedUserId && !clearAssignment) {
      toast.error('Specifica un negozio, un collaboratore o spunta "Rimuovi assegnazione"');
      return;
    }
    setSaving(true);
    try {
      await onConfirm({ storeId, assignedUserId, clearAssignment });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent data-testid="bulk-reassign-dialog">
        <DialogHeader>
          <DialogTitle>Sposta {count} {count === 1 ? 'oggetto' : 'oggetti'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {isOps && (
            <div>
              <Label className="text-xs">Nuovo negozio (opzionale)</Label>
              <Select value={storeId || 'none'} onValueChange={(v) => setStoreId(v === 'none' ? '' : v)}>
                <SelectTrigger data-testid="bulk-reassign-store">
                  <SelectValue placeholder="Seleziona negozio" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— non cambiare —</SelectItem>
                  {stores.map(s => (
                    <SelectItem key={s.store_id} value={s.store_id}>
                      {s.display_name || s.name}
                      {(s.kind === 'warehouse' || s.kind === 'academy') && (
                        <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-500">Sede</span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 mt-1">
                Spostando in un nuovo negozio l'assegnazione precedente viene rimossa.
              </p>
            </div>
          )}
          <div>
            <Label className="text-xs">Affida a (opzionale)</Label>
            <Select
              value={assignedUserId || 'none'}
              onValueChange={(v) => {
                setAssignedUserId(v === 'none' ? '' : v);
                if (v !== 'none') setClearAssignment(false);
              }}
            >
              <SelectTrigger data-testid="bulk-reassign-user">
                <SelectValue placeholder={assigneesLoading ? 'Caricamento…' : 'Seleziona collaboratore'} />
              </SelectTrigger>
              <SelectContent className="max-h-[50vh]">
                <SelectItem value="none">— nessuno —</SelectItem>
                {grouped.sameStoreStaff.length > 0 && (
                  <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-slate-500 font-semibold bg-slate-50 sticky top-0">
                    Staff del negozio
                  </div>
                )}
                {grouped.sameStoreStaff.map(a => (
                  <SelectItem key={a.id} value={a.id}>
                    <span className="font-medium">{a.name}</span>
                    {a.role && <span className="ml-2 text-[10px] text-slate-500 uppercase tracking-wider">{a.role}</span>}
                  </SelectItem>
                ))}
                {grouped.otherStaff.length > 0 && (
                  <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-slate-500 font-semibold bg-slate-50">
                    Altri staff
                  </div>
                )}
                {grouped.otherStaff.map(a => (
                  <SelectItem key={a.id} value={a.id}>
                    <span className="font-medium">{a.name}</span>
                    {a.store_name && <span className="ml-2 text-[10px] text-slate-500">· {a.store_name}</span>}
                  </SelectItem>
                ))}
                {grouped.managers.length > 0 && (
                  <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-slate-500 font-semibold bg-slate-50">
                    Manager
                  </div>
                )}
                {grouped.managers.map(a => (
                  <SelectItem key={a.id} value={a.id}>
                    <span className="font-medium">{a.name}</span>
                    <span className="ml-2 text-[10px] text-slate-500 uppercase tracking-wider">{a.role}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!assigneesLoading && assignees.length === 0 && (
              <p className="text-[11px] text-slate-500 mt-1">
                Nessun collaboratore disponibile.
              </p>
            )}
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={clearAssignment}
              onChange={(e) => {
                setClearAssignment(e.target.checked);
                if (e.target.checked) setAssignedUserId('');
              }}
              className="w-4 h-4 accent-slate-900"
              data-testid="bulk-clear-assignment"
            />
            <span>Rimuovi assegnazione corrente</span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={saving}>Annulla</Button>
          <Button onClick={handleConfirm} disabled={saving} data-testid="bulk-reassign-confirm">
            {saving ? 'Salvataggio…' : 'Conferma'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
